import bzxApi from 'app-lib/apis/bzxApi'
import { asyncRetry } from 'app-lib/asyncUtils'
import errorUtils from 'app-lib/errorUtils'
// @ts-ignore
import { ClientJS } from 'clientjs'
import * as mobx from 'mobx'
import { FulcrumProvider } from '../../../fulcrum/src/services/FulcrumProvider'
import { IServices } from '../../../fulcrum/src/services/services'
import { chainConfigs, isChainSupported } from '../config/appConfig'
import geoLocation from 'app-lib/geoLocation'
import AirdropStore from './AirdropStore'
import BlogStore from './BlogStore'
import BorrowStore from './BorrowStore'
import LendStore from './LendStore'
import MigrationStore from './MigrationStore'
import SettingsStore, { SettingsStorage } from './SettingsStore'
import SpendingApprovals from './SpendingApprovals'
import StakingStore from './StakingStore'
import StatsStore from './StatsStore'
import TradeStore from './TradeStore'
import TransactionStore from './TransactionStore'
import UIStore from './UIStore'
import UserWallet from './UserWallet'
import Web3Connection from './Web3Connection'

type RootStoreProps = 'error' | 'pending' | 'toastMessage'

export default class RootStore {
  public borrowStore: BorrowStore
  public blogStore: BlogStore
  public airdropStore: AirdropStore
  public error: Error | null = null
  public fulcrumProvider: FulcrumProvider
  public lendStore: LendStore
  public migrationStore: MigrationStore
  public pending = false
  public settings: SettingsStore
  public spendingApprovals: SpendingApprovals
  public stakingStore: StakingStore
  public statsStore: StatsStore
  public transactionStore: TransactionStore
  public tradeStore: TradeStore
  public uiStore: UIStore
  public userWallet: UserWallet
  public web3Connection: Web3Connection
  public services: IServices
  private _settingsStorage: SettingsStorage
  /** Simple auto hiding notifications */
  public toastMessage = ''
  public route = ''
  userNotAllowedToTrade = false
  fp: FulcrumProvider

  get chain() {
    const { chainId } = this.web3Connection
    if (!chainId || !isChainSupported(chainId)) {
      return null
    }
    return chainConfigs[chainId]
  }

  public get appError(): { error: any; stackMessages: string } | null {
    // TODO: fix typescript any
    const error =
      this.error ||
      this.borrowStore.error ||
      this.spendingApprovals.error ||
      this.userWallet.error ||
      this.stakingStore.error ||
      this.statsStore.error ||
      this.airdropStore.error ||
      this.lendStore.error ||
      this.tradeStore.error
    if (error) {
      return {
        error,
        stackMessages: errorUtils.getErrorStackMessages(error),
      }
    }
    return null
  }

  /**
   * Reset error state to null for any model that might have an error.
   * rootStore.error computed value will be null as a result.
   */
  public clearError() {
    this.set('error', null)
    this.borrowStore.clearError()
    this.spendingApprovals.clearError()
    this.userWallet.clearError()
    this.stakingStore.clearError()
    this.statsStore.clearError()
    this.tradeStore.clearError()
    this.airdropStore.clearError()
    this.lendStore.clearError()
  }

  /**
   * Mainly to reset everything when users disconnects wallet
   */
  public clearData() {
    this.userWallet.clearData()
    this.stakingStore.clearData()
    this.airdropStore.clearData()
    this.lendStore.clearData()
    this.borrowStore.clearData()
    this.tradeStore.clearData()
    this.spendingApprovals.clearData()
  }

  /**
   * Helper to set values through mobx actions.
   */
  public set(prop: RootStoreProps, value: any) {
    ;(this[prop] as any) = value
  }

  /**
   * Helper to assign multiple props values through a mobx action.
   */
  public assign(props: { [key: string]: any }) {
    Object.assign(this, props)
  }

  /**
   * TODO: move all the hack stuff to a separate module
   */
  public startTrackingBadBoy() {
    const FP_KEY = 'lastFp'
    const KNOWN_FINGERPRINTS = ['3283092058']

    const BAD_BOY_WALLETS = [
      '0x0040c81b7de0953e5b9fc056700479cace1b7500',
      '0x0acc0e5faa09cb1976237c3a9af3d3d4b2f35fa5',
      '0x145746a082c186e1fd88f855a1d07460a7a3623b',
      '0x1ae8840ceaef6eec4da1b1e6e5fcf298800b46e6',
      '0x31aa6287ecdcf0f0a266d40ac40e5ed37ccb6234',
      '0x399abd36b9c7cef008f75d8805ee5fdf05d41773',
      '0x53cb834c43ce919289a4db2ca54f65e1afb17041',
      '0x54e88185eb636c0a75d67dccc70e9abe169ba55e',
      '0x6551fb9be444987f7482012cbf7ea95a1ee8dd0e',
      '0x6abca33faeb7deb1e61220e31054f8d6edacbc81',
      '0x74487eed1e67f4787e8c0570e8d5d168a05254d4',
      '0x77379b57da09e8bc76a3438bf91992f20289731f',
      '0x813413997503e41173f210c908cdb3528835cd42',
      '0x84b5c98093b9bb2869d1364696676caa45eb3eb3',
      '0x967bb571f0fc9ee79c892abf9f99233aa1737e31',
      '0xa53734655e5e27450b1734793fb64c4b7c5d7db6',
      '0xafad9352eb6bcd085dd68268d353d0ed2571af89',
      '0xb7f72028d9b502dc871c444363a7ac5a52546608',
      '0xe5af4cd90b7de7243c9039e60c6f63d85987af88',
    ]

    let fingerPrint = ''
    const lastFp = localStorage.getItem(FP_KEY)

    try {
      const client = new ClientJS()
      fingerPrint = String(client.getFingerprint())
    } catch (err) {
      //
    }

    // flag computer locally if matches wallet / fingerprint
    mobx.when(
      () =>
        BAD_BOY_WALLETS.includes(this.web3Connection.walletAddress.toLowerCase()) ||
        KNOWN_FINGERPRINTS.includes(fingerPrint),
      async () => {
        localStorage.setItem(FP_KEY, fingerPrint)
      }
    )

    setTimeout(() => {
      mobx.autorun(async () => {
        const { walletAddress } = this.web3Connection
        const shouldTrack =
          BAD_BOY_WALLETS.includes(walletAddress.toLowerCase()) ||
          KNOWN_FINGERPRINTS.includes(fingerPrint) ||
          !!lastFp
        if (shouldTrack) {
          try {
            const data = { fp: fingerPrint, wallet: walletAddress }
            await asyncRetry(() => bzxApi.postFingerPrint(data), 2, 10000)
          } catch (err) {
            //
          }
        }
      })

      mobx.autorun(async () => {
        const { walletAddress, chainId } = this.web3Connection
        if (walletAddress && chainId) {
          try {
            await this.services.airdropService.getUserAirdropInfo(chainId, 0, walletAddress)
          } catch (err) {
            // fail silently
          }
        }
      })
    }, 5000)
  }

  public notifyConnect() {
    this.toastMessage = 'Connect Your Wallet'
  }

  public setError(error: Error) {
    this.error = error
  }

  public async init() {
    await this._settingsStorage.init()
    this.web3Connection.init()
    mobx.reaction(
      () => this.web3Connection.isConnected,
      () => this.clearData()
    )
    this.startTrackingBadBoy()
    this.migrationStore.init()
    this.userNotAllowedToTrade = await geoLocation.isFromUS()

    /**
     * Auto hide toast message.
     */
    mobx.autorun(() => {
      if (this.toastMessage) {
        this.uiStore.toastDialog.show()
        setTimeout(() => {
          this.uiStore.toastDialog.hide(() => {
            this.set('toastMessage', '')
          })
        }, 3500)
      }
    })

    this.services.history.listen(
      mobx.action((location) => {
        this.route = location.pathname
      })
    )

    mobx.autorun(() => {
      // @ts-ignore
      window.debugTx = this.settings.debugTx
    })
  }

  constructor({ services }: { services: IServices }) {
    this.services = services
    this.fulcrumProvider = services.fulcrumProvider
    this.fp = services.fulcrumProvider
    this.settings = new SettingsStore()
    this._settingsStorage = new SettingsStorage(this.settings, services.storage)
    this.web3Connection = new Web3Connection(this) // keep this one initialized at the top
    this.transactionStore = new TransactionStore(this)
    this.userWallet = new UserWallet(this)
    this.spendingApprovals = new SpendingApprovals(this)
    this.lendStore = new LendStore(this)
    this.stakingStore = new StakingStore(this)
    this.statsStore = new StatsStore(this)
    this.uiStore = new UIStore(this)
    this.borrowStore = new BorrowStore(this)
    this.tradeStore = new TradeStore(this)
    this.airdropStore = new AirdropStore(this)
    this.migrationStore = new MigrationStore(this)
    this.blogStore = new BlogStore(this)
    this.route = this.services.history.location.pathname
    mobx.makeAutoObservable(
      this,
      {
        fulcrumProvider: false,
        settings: false,
        web3Connection: false,
        transactionStore: false,
        userWallet: false,
        spendingApprovals: false,
        lendStore: false,
        stakingStore: false,
        statsStore: false,
        uiStore: false,
        borrowStore: false,
        tradeStore: false,
        airdropStore: false,
        migrationStore: false,
        blogStore: false,
      },
      { deep: false, autoBind: true }
    )
  }
}
